import React from 'react';
import classNames from 'classnames';
import s from './RadioButton.module.scss';
import { TypeBadge } from '@/core/generated-types';
import { DynamicComponent } from '../DynamicComponent';

interface RadioButtonProps {
  id: string | number;
  Content: string | React.ComponentType;
  fillType?: 'ring' | 'round';
  textColor?: 'primary' | 'white' | 'default';
  radioAlignment?: 'top' | 'middle';
  className?: string;
  radioLabelClass?: string;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string | number,
  ) => void;
  checked?: boolean;
  disabled?: boolean;
  boldLabel?: boolean;
  children?: React.ReactNode;
  badge?: TypeBadge;
  tightenStyles?: boolean;
}

const RadioButton: React.FC<RadioButtonProps> = ({
  id,
  Content,
  fillType = 'ring',
  textColor = 'default',
  className = '',
  radioLabelClass = '',
  onChange,
  checked = false,
  disabled = false,
  boldLabel,
  children,
  badge,
  tightenStyles = false,
  ...rest
}) => {
  const radioClasses = classNames(s.radioInput, s[fillType], {
    [s.disabled]: disabled,
  });

  const wrapperClasses = classNames(s.radioWrapper, s[textColor], className, {
    // Base tightened styles - unselected state
    'border-t-2 border-l-2 border-r-2 first:rounded-t-md last:rounded-b-md last:border-b-2 p-4 border-gray-midtone':
      tightenStyles && !checked,
    // Selected state with complete border
    'border-2 first:rounded-t-md last:rounded-b-md p-4 border-blue-deep bg-indigo-pastel':
      tightenStyles && checked,
    // Standard styles
    'border rounded-md mb-4 last:mb-0 px-4 py-4': !tightenStyles,
    'border-gray-500': !checked && !tightenStyles,
    'border-blue-deep bg-indigo-pastel outline outline-blue-deep':
      checked && !tightenStyles,
    [s.boldLabel]: boldLabel,
    [s.hasBadge]: !!badge,
  });

  return (
    <label className={wrapperClasses}>
      {badge && (
        <div className="absolute -top-4 -translate-x-1/2 left-1/2">
          <DynamicComponent component={badge} />
        </div>
      )}
      <input
        type="radio"
        value={id}
        checked={checked}
        onChange={(e) => onChange && onChange(e, id)}
        className={radioClasses}
        id={id.toString()}
        disabled={disabled}
        {...rest}
      />
      <div className={s.radioFill} />
      <div className={s.label}>
        {typeof Content === 'string' ? (
          <p className={`${s.text} ${radioLabelClass}`}> {Content} </p>
        ) : (
          <>{typeof Content === 'function' ? <Content /> : Content}</>
        )}
        {children}
      </div>
    </label>
  );
};

export default React.memo(RadioButton);
